import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { authFetch } from '../lib/authFetch'

export interface User {
  id: string
  email: string
  role: string
  name?: string
  picture_url?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => void
  logout: () => Promise<void>
  hasRole: (role: string) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initial session check
  useEffect(() => {
    authFetch('/auth/me', { noAutoRedirect: true })
      .then(async (res) => {
        setUser(res.ok ? ((await res.json()) as User) : null)
      })
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false))
  }, [])

  // Clear user when any API call returns 401 (session expiry)
  useEffect(() => {
    const handleUnauthorized = () => setUser(null)
    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const res = await authFetch('/auth/login', {
      method: 'POST',
      json: { email, password },
      noAutoRedirect: true,
    })
    if (!res.ok) {
      let message = 'Invalid email or password'
      try {
        const data = (await res.json()) as { error?: string }
        if (data.error) message = data.error
      } catch {
        // ignore parse error
      }
      throw new Error(message)
    }
    // API returns user fields directly: { id, email, name, role }
    const data = (await res.json()) as User
    setUser(data)
  }, [])

  const loginWithGoogle = useCallback((): void => {
    window.location.href = '/auth/google'
  }, [])

  const logout = useCallback(async (): Promise<void> => {
    await authFetch('/auth/logout', { method: 'POST', noAutoRedirect: true }).catch(() => undefined)
    setUser(null)
    // Navigation handled in AppLayout when user becomes null
  }, [])

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!user) return false
      if (user.role === 'SUPER_ADMIN') return true
      return user.role === role
    },
    [user],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        loginWithGoogle,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
